import {
  Accordion,
  Button,
  ButtonGroup,
  Card,
  ErrorSummary,
  Heading,
  Hero,
  Notification,
  Page,
  TextInput,
  useAccessTokenManagement
} from '@hbo/ui-servicing-react-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useBlocker, useLocation, useNavigate } from 'react-router-dom';
import { AuthenticationService } from 'services/authentication';
import { OneTimePasscodeService } from 'services/oneTimePasscode';
import { RoutePath } from 'types/global';
import { onKeyDownNumericInputOnly } from 'utils/validation';
import { schema } from './config';

interface Schema {
  otp?: string;
}

type LocationState = AuthenticationService.Payload & AuthenticationService.Response;

const OneTimePasscode: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { issueToken, isIssuingToken, isAuthenticated, isAuthenticating } =
    useAccessTokenManagement();
  // const { track } = useDataLayer();

  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false);

  const state = location.state as LocationState;

  // let isBlocked = true;

  /** Block navigation to any other page */
  // useBlocker(() => {
  //   return isBlocked;
  // });

  const {
    mutate,
    isPending: isLoading,
    isError,
    error
  } = useMutation<
    OneTimePasscodeService.ValidateResponse,
    AxiosError,
    OneTimePasscodeService.ValidatePayload
  >({
    mutationFn: (data) => OneTimePasscodeService.validate(data),
    onSuccess: ({ customStatusCode, token }) => {
      if (customStatusCode === OneTimePasscodeService.CustomStatusCode.ServiceDown) {
        return navigate(RoutePath.OneTimePasscodeServiceDown, { replace: true });
      }

      if (customStatusCode === OneTimePasscodeService.CustomStatusCode.AccountLocked) {
        return navigate(RoutePath.Locked, { replace: true, state: { reason: 'OTP' } });
      }

      issueToken(
        {
          token,
          scope: ['a4ir203:mortgages-v1:case-tracking'],
          redirectUri: 'urn:ietf:wg:oauth:2.0:auto:oo'
        },
        {
          onSuccess: () => navigate(RoutePath.CaseTracking, { replace: true })
        }
      );
    }
  });

  const {
    mutate: resend,
    isPending: isResending,
    isError: isResendError,
    error: resendError,
    isSuccess,
    data: resendData,
    reset
  } = useMutation<
    OneTimePasscodeService.ResendResponse,
    AxiosError,
    OneTimePasscodeService.ResendPayload
  >({
    mutationFn: (data) => OneTimePasscodeService.resend(data),
    onSuccess: ({ resendDisable }) => setIsResendDisabled(resendDisable)
  });

  const { control, handleSubmit } = useForm({
    mode: 'onBlur',
    //@ts-ignore
    resolver: yupResolver(schema)
  });

  const onSubmit: SubmitHandler<Schema> = (data) => {
    reset();
    const { accountNumber, postcode, dob, surname } = state;
    return mutate({
      authenticationRequest: { accountNumber, postcode, dob, surname },
      otpCode: data.otp
    });
  };

  useEffect(() => {
    document.querySelector<HTMLElement>('h2')?.setAttribute('tabIndex', '-1');
    document.querySelector<HTMLElement>('h2')?.focus();
  }, []);

  if (!state?.mobileNumber) {
    return <Navigate to={RoutePath.ConfirmMobileNumber} replace />;
  }

  if (isAuthenticated) {
    return <Navigate to={RoutePath.CaseTracking} replace/>;
  }

  return (
    <>
      <Helmet>
        <title>{t('oneTimePasscode.tabTitle')}</title>
      </Helmet>
      <Hero>
        <Hero.PrimaryContent title={t('oneTimePasscode.title')} />
      </Hero>
      <Page>
        <Page.Content>
          <Heading level={3} as="h2" alternative>
            {t('oneTimePasscode.heading1')}
          </Heading>
          <p>{t('oneTimePasscode.line1')}</p>
          <p>{t('oneTimePasscode.line2')}</p>
          <p>{t('oneTimePasscode.line3')}</p>
        </Page.Content>
        <Page.Content>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Card>
              <Card.Header>{t('oneTimePasscode.heading2')}</Card.Header>
              <Card.Body>
                {isError && (
                  <ErrorSummary title="">
                    <span className="sr-only">{t('generic.screenReaderError')}</span>
                    {error.message}
                  </ErrorSummary>
                )}
                <Controller
                  render={({
                    field: { onChange, onBlur, value, name, ref },
                    fieldState: { error }
                  }) => (
                    <TextInput
                      label={t('oneTimePasscode.label.oneTimePasscode')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ref={ref}
                      error={error?.message}
                      inputProps={{
                        maxLength: 6,
                        onKeyDown: onKeyDownNumericInputOnly,
                        pattern: '\\d*'
                      }}
                    />
                  )}
                  name="otp"
                  control={control}
                />
                <Accordion title={t('oneTimePasscode.accordion.title')} alternative>
                  {isResendError && <ErrorSummary title="">{resendError.message}</ErrorSummary>}
                  <ButtonGroup>
                    <Button
                      variant="secondary"
                      disabled={isResendDisabled || isLoading}
                      isLoading={isResending}
                      onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                        const pageName = t('oneTimePasscode.tabTitle');
                        // track(
                        //   `CLICK: ${pageName} - ${t('oneTimePasscode.resendPasscode')} - Button`,
                        //   getInternalLinkElement(event),
                        //   pageName
                        // );
                        const { accountNumber, postcode, dob, surname } = state;
                        return resend({
                          accountNumber,
                          postcode,
                          dob,
                          surname
                        });
                      }}
                    >
                      {t('oneTimePasscode.resendPasscode')}
                    </Button>
                    <div role="alert">
                      {isSuccess && (
                        <p>
                          {t('oneTimePasscode.resendCount', { count: resendData.otpResendCount })}
                        </p>
                      )}
                    </div>
                  </ButtonGroup>
                  {isResendDisabled && (
                    <Notification variant="warning">
                      <span className="sr-only">{t('generic.screenReaderWarn')}</span>
                      {t('oneTimePasscode.error.maxResendRequests')}
                    </Notification>
                  )}
                  <p>{t('oneTimePasscode.accordion.line1')}</p>
                </Accordion>
              </Card.Body>
            </Card>
            <ButtonGroup>
              <Button
                type="submit"
                data-cy="continue-button"
                isLoading={isLoading}
                onClick={(event: React.MouseEvent<Element, MouseEvent>) => {
                  const pageName = t('oneTimePasscode.tabTitle');
                  // track(
                  //   `CLICK: ${pageName} - ${t('generic.goToMortgageSummary')} - Button`,
                  //   getInternalLinkElement(event),
                  //   pageName
                  // );
                }}
              >
                {t('generic.goToMortgageSummary')}
              </Button>
            </ButtonGroup>
          </form>
        </Page.Content>
      </Page>
    </>
  );
};

export default OneTimePasscode;
